import { useFrontendTool } from "@copilotkit/react-core"
import { useHistory } from "react-router"
import farmSo from "stores/farm"
import { getUrlByPageId } from "stores/route/utils/pagesId"
import { PAGE_ID } from "stores/route/utils/types"
import { COMPONENT_TYPE } from "types/Service"
import { getFarmId, waitNavigate } from "./utils"
import { TOOLS } from "./utils/types"
import { getComponentsDescriptions, getFarmsSectionsDescriptions, getGrowUnitsSectionsDescriptions } from "./utils/sectionsDescriptions"



/**
 * TOOL che permette di cambiare pagina  
 * Contiene anche la descrizione di ogni pagina
 */
export function useNavigate() {

	const history = useHistory()

	useFrontendTool({
		name: TOOLS.NAVIGATE_IN_FARM_SECTION,
		description: `Visualizza e seleziona una FARM e, se specificata, visualizza una FARM-SECTION della FARM stessa.
`,
		parameters: [
			{
				name: "farm_id",
				type: "string",
				description: `'id' identificativo numerico di una FARM oppure il suo 'code' oppure parte del suo 'name'`,
				required: true,
			},
			{
				name: "farm_section",
				type: "string",
				description: `Il codice della FARM-SECTION da visualizzare.
Se non specificato visualizzo '${PAGE_ID.FARM_MAP}'.
Le FARM-SECTION posso essere:
${getFarmsSectionsDescriptions()}
`,
				required: false,
			},

		],
		handler: async ({ farm_id, farm_section }) => {
			if (!farm_id) return {
				success: false,
				error: "Il parametro 'farm_id' è obbligatorio"
			}
			let farmId: number = getFarmId(farm_id)
			if (!farmId) return {
				success: false,
				error: `Nessuna farm trovata con 'id' o 'code' o nome che corrisponda a "${farm_id}"`
			}
			const section = farm_section ?? PAGE_ID.FARM_MAP
			const url = getUrlByPageId(section, farmId)
			return await waitNavigate(url, history)
		},
	})

	useFrontendTool({
		name: TOOLS.NAVIGATE_IN_GROWUNIT_SECTION,
		description: `Visualizza e seleziona un COMPONENT di tipo "grow-unit" GROWUNIT e, se specificata, una GROWUNIT-SECTION della GROWUNIT stessa.
		`,
		parameters: [
			{
				name: "component_code",
				type: "string",
				description: "codice identificativo della GROWUNIT da visualizzare",
				required: true,
			},
			{
				name: "growunit_section",
				type: "string",
				description: `Codice della GROWUNIT-SECTION da visualizzare.
Se non specificato visualizzo la growunit-home
Le GROWUNIT-SECTION sono:
${getGrowUnitsSectionsDescriptions()}
`,
				required: false,
			},

		],
		handler: async ({ component_code, growunit_section }) => {
			if (!component_code) return {
				success: false,
				error: "Il parametro 'component_code' è obbligatorio"
			}
			const farm = farmSo.getByGrowunitCode(component_code)
			if (!farm) return {
				success: false,
				error: `Nessuna farm trovata con una growunit che abbia code "${component_code}"`
			}
			const section = growunit_section ?? PAGE_ID.GROWUNIT_HOME
			const url = getUrlByPageId(section, farm.id, component_code)
			return await waitNavigate(url, history)
		},
	})

	useFrontendTool({
		name: TOOLS.NAVIGATE_IN_COMPONENT,
		description: `Visualizza un COMPONENT generico di qualsiasi COMPONENT-TYPE.
		`,
		parameters: [
			{
				name: "component_type",
				type: "string",
				description: `Tipo COMPONENT-TYPE del COMPONENT da visualizzare.
Può essere: 
${getComponentsDescriptions()}
`,
				required: true,
			},
			{
				name: "component_code",
				type: "string",
				description: `Codice identificativo specifico del COMPONENT da visualizzare.`,
				required: true,
			},
			{
				name: "farm_id",
				type: "number",
				description: `Identificativo della FARM che contiene il COMPONENT da visualizzare. Se non specificata usa la FARM selezionata.`,
				required: false,
			},
		],
		handler: async ({ component_type, component_code, farm_id }) => {
			if (!component_type || !component_code) return {
				success: false,
				error: "Il parametro 'component_type' e 'component_code' sono obbligatori"
			}
			farm_id = getFarmId(farm_id) ?? farmSo.state.select?.id
			const farm = farmSo.getByPartialComponent({ type: component_type as any, code: component_code })[0]?.farm
			if (!farm) return {
				success: false,
				error: `Nessuna FARM con COMPONENT type: "${component_type}" e code: "${component_code}"`
			}
			const pageId:PAGE_ID = {
				[COMPONENT_TYPE.GERMINATOR]: PAGE_ID.COMPONENT_GERMINATION,
				[COMPONENT_TYPE.HVAC]: PAGE_ID.COMPONENT_HVAC,
				[COMPONENT_TYPE.OSMOTIC_WATER]: PAGE_ID.COMPONENT_OSMOTIC_WATER,
				[COMPONENT_TYPE.CO2]: PAGE_ID.COMPONENT_CO2,
				[COMPONENT_TYPE.WATER_TREATMENT]: PAGE_ID.COMPONENT_WATER_TREATMENT,
				[COMPONENT_TYPE.THERMAL_POWER_PLANT]: PAGE_ID.COMPONENT_THERMAL_POWER_PLANT,
				[COMPONENT_TYPE.CLIMATE]: PAGE_ID.GROWUNIT_CLIMATE,
			}[component_type as COMPONENT_TYPE]
			const url = getUrlByPageId(pageId, farm.id, component_code)
			return await waitNavigate(url, history)
		},
	})


	//#region CROPS AND RECIPES

	useFrontendTool({
		name: TOOLS.NAVIGATE_CROP_DETAILS,
		description: `Visualizza il dettaglio di uno specifico CROP.`,
		parameters: [
			{
				name: "crop_id",
				type: "string",
				description: "Codice identificativo (id) del CROP da visualizzare",
				required: true,
			},
		],
		handler: async ({ crop_id }) => {
			const url = getUrlByPageId(PAGE_ID.CROPS_RECIPES_DETAIL, crop_id)
			return await waitNavigate(url, history)
		},
	})

	useFrontendTool({
		name: TOOLS.NAVIGATE_RECIPE_DETAILS,
		description: `Visualizza il dettaglio di uno specifico RECIPE.`,
		parameters: [
			{
				name: "recipe_id",
				type: "string",
				description: "Codice identificativo (id) del RECIPE da visualizzare",
				required: true,
			},
		],
		handler: async ({ recipe_id }) => {
			const url = getUrlByPageId(PAGE_ID.RECIPES_DETAIL, recipe_id)
			return await waitNavigate(url, history)
		},
	})

	//#endregion

}

